import { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { ListingFilter } from '../../domain/models/Filter';
import { TYPE_FILTER_STRIP_ASSETS } from '../../constants/typeFilterStripAssets';
import { TYPE_FILTER_STRIP_MAPPING } from '../../constants/typeFilterStripMapping';
import { getTypeFilterStripUrl } from '../../utils/typeFilterStripUrl';

const normalizeName = (name: string) => name.trim().replace(/\s+/g, ' ').toLowerCase();

function TypeFilterIcon({ src, eager = false }: { src: string; eager?: boolean }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || eager || visible) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      setVisible(true);
      observer.disconnect();
    }, { root: element.closest('nav') });
    observer.observe(element);
    return () => observer.disconnect();
  }, [eager, visible]);

  return (
    <span ref={ref} className="col-start-1 col-end-4 row-start-1 h-[clamp(1.75rem,10cqw,3rem)] w-[clamp(1.75rem,10cqw,3rem)]">
      {(eager || visible) && <img src={src} alt="" decoding="async" className="h-full w-full object-contain" />}
    </span>
  );
}

// Asset map se fixed visual catalogue banta hai. "All" alag render hota hai;
// baaki labels normalized category ke saath tiles bante hain, e.g. "Rings" -> "rings".
const typeTiles = Object.entries(TYPE_FILTER_STRIP_ASSETS)
  .filter(([label]) => label !== 'All')
  .map(([label, imageUrl]) => ({ label, imageUrl, category: normalizeName(label) }));

export function TypeFilterStrip({ filters = [] }: { filters?: ListingFilter[] }) {
  // Flow example used below: filter sheet mein Rings, 18k Gold aur Akshaya Tritiya selected hain.
  // `filters` preview API response hai, isliye teeno items apni categories mein selected: true honge.

  // Chip panel ko accessible unique ID milta hai. `expandedOwner` null ho to panel closed hai;
  // example mein Rings ka arrow click hone ke baad iska value "rings" hoga.
  const chipsId = useId();
  const stripRef = useRef<HTMLElement>(null);
  const [expandedOwner, setExpandedOwner] = useState<string | null>(null);

  const handleTileClick = () => {
    setExpandedOwner(null);
    stripRef.current?.scrollTo({ left: 0, behavior: 'auto' });
  };

  // Sab filter categories mein se sirf Type category niklegi.
  // Example: typeOptions mein Rings hoga; 18k Gold aur Akshaya Tritiya yahan nahi aayenge.
  const typeOptions = filters.find((filter) => normalizeName(filter.category) === 'type')?.items ?? [];
  // Type ke andar selected: true items rakho. Example result: [Rings].
  const selectedTypes = typeOptions.filter((item) => item.selected);

  // Exactly one Type selected hai, isliye uska normalized naam "rings" banega.
  // Zero ya multiple Types hote to empty string milti aur later `All` active hota.
  const selectedTypeName = selectedTypes.length === 1
    ? normalizeName(selectedTypes[0].displayName || selectedTypes[0].name).replace(/\+/g, ' ')
    : '';
  // Subtype ho to visual group mapping lagti hai; direct Rings ki mapping nahi hai,
  // so fallback selectedTypeName use hota hai aur selectedCategory "rings" rehta hai.
  const selectedCategory = selectedTypeName
    ? normalizeName(TYPE_FILTER_STRIP_MAPPING[selectedTypeName] ?? selectedTypeName)
    : '';
  // Static tiles copy ki jaati hain; current API ne options narrow kiye hon tab bhi all icons visible rehte hain.
  const items = [...typeTiles];

  // "rings" tile ka index milta hai. Match milne par allSelected false hota hai,
  // aur Rings tile ko All ke immediately baad lane ke liye array ke start par move karte hain.
  const selectedIndex = items.findIndex(({ category }) => category === selectedCategory);
  const allSelected = selectedIndex < 0;
  if (selectedIndex > 0) items.unshift(...items.splice(selectedIndex, 1));

  // Ab every filter category ko iterate karke removable chips bante hain.
  // Example mein Rings already active tile se represent ho raha hai, so complete Type category skip hoti hai.
  // Gold/Purity category se selected 18k Gold aur occasion/collection category se selected
  // Akshaya Tritiya map hoke final selectedChips = [18k Gold, Akshaya Tritiya] banta hai.
  const selectedChips = filters.flatMap((filter) => {
    if (normalizeName(filter.category) === 'type' && !allSelected) return [];
    return filter.items.filter((item) => item.selected).map((item) => ({ category: filter.category, item }));
  });

  // Example mein All active nahi hai, so chipOwner "rings" hoga.
  // Arrow click se expandedOwner bhi "rings" hote hi chipsOpen true aur panel visible hota hai.
  const chipOwner = allSelected ? 'all' : selectedCategory;
  const chipsOpen = expandedOwner === chipOwner;

  // Arrow navigation Link ke bahar hai: first click owner set karke panel kholta hai,
  // next click null set karke close karta hai; open state mein chevron rotate hota hai.
  const renderChipToggle = (label: string) => (
    <button
      type="button"
      aria-label={`${chipsOpen ? 'Hide' : 'Show'} selected filters for ${label}`}
      aria-expanded={chipsOpen}
      aria-controls={chipsId}
      onClick={() => setExpandedOwner(chipsOpen ? null : chipOwner)}
      className="col-start-3 row-start-2 flex h-full w-full min-w-0 items-center justify-center text-[#14366D] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#14366D]"
    >
      <ChevronDown aria-hidden="true" className={`h-[1em] w-[1em] shrink-0 transition-transform ${chipsOpen ? 'rotate-180' : ''}`} />
    </button>
  );

  // `selected=true` sirf example ke Rings tile ko blue background, bold text aur blue border dega;
  // every other tile normal rahega. Minimum 20% width ke baad long label ke liye tile grow kar sakta hai.
  const itemClass = (selected: boolean) => [
    'grid min-w-[20%] w-max flex-none grid-cols-[0.9em_max-content_0.9em] grid-rows-[auto_1fr] items-center justify-center justify-items-center gap-x-[0.15em] gap-y-[0.15em] border-b-2 px-[0.05cqw] py-[1cqw] text-[12px] text-[#14366D]',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-3px] focus-visible:outline-[#14366D]',
    selected ? 'border-[#14366D] bg-[#DCEFFF] font-bold' : 'border-transparent font-normal',
  ].join(' ');

  return (
    <section>
      {/* All + complete type catalogue ek horizontally scrollable row mein render hota hai. */}
      <nav ref={stripRef} aria-label="Filter by jewellery type" className="flex [container-type:inline-size] overflow-x-auto overscroll-x-contain [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] bg-white">
        {/* Example mein allSelected false hai, so All normal tile rahega aur is par arrow render nahi hoga. */}
        <div className={itemClass(allSelected)}>
          <Link to={getTypeFilterStripUrl()} onClick={handleTileClick} aria-current={allSelected ? 'true' : undefined} className="contents">
            <TypeFilterIcon src={TYPE_FILTER_STRIP_ASSETS.All} eager />
            <span className="col-start-2 row-start-2 flex min-h-[2.4em] items-center justify-center text-center leading-[1.2]">All</span>
          </Link>
          {allSelected && selectedChips.length > 0 && renderChipToggle('All')}
        </div>
        {/* `items` mein Rings first hai. Uski category selectedCategory se match karegi,
            so Rings active style lega aur selectedChips present hone ki wajah se us par arrow dikhega. */}
        {items.map(({ imageUrl, label, category }, index) => (
          <div key={category} className={itemClass(category === selectedCategory)}>
          <Link
            to={getTypeFilterStripUrl(label)}
            aria-current={category === selectedCategory ? 'true' : undefined}
            onClick={handleTileClick}
            className="contents"
          >
            <TypeFilterIcon src={imageUrl} eager={index < 4} />
            <span title={label} className="col-start-2 row-start-2 block whitespace-nowrap text-center leading-[1.2]">{label}</span>
          </Link>
          {category === selectedCategory && selectedChips.length > 0 && renderChipToggle(label)}
          </div>
        ))}
      </nav>
      {/* Rings arrow open hone par selectedChips map hoke [18k Gold x] [Akshaya Tritiya x]
          render honge. Har chip ka backend `item.url` us filter ko remove karke navigation karega. */}
      {selectedChips.length > 0 && (
        <div id={chipsId} hidden={!chipsOpen} aria-label="Selected filters" className={`${chipsOpen ? 'flex' : 'hidden'} gap-2 overflow-x-auto overscroll-x-contain [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] bg-[#DCEFFF] px-4 py-3`}>
          {selectedChips.map(({ category, item }) => (
            <Link
              key={`${category}:${item.name}`}
              to={item.url || '#'}
              aria-label={`Remove ${item.displayName || item.name}`}
              aria-disabled={!item.url || undefined}
              onClick={(event) => { if (!item.url) event.preventDefault(); }}
              className="flex shrink-0 items-center gap-2 rounded-full border border-[#B5D1FF] bg-[#F2F8FF] px-3 py-2 text-[12px] text-[#14366D]"
            >
              <span className="whitespace-nowrap">{item.displayName || item.name}</span>
              <span aria-hidden="true">×</span>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
